/**
 * 보고서 PDF 페이지 넘김 — 한곳에서 조절하는 값.
 *
 * html2pdf는 캔버스를 A4 비율로 자른다. CSS `page-break`와
 * `pagebreak.avoid: '.report-table'`은 페이지 높이(고정 px)가
 * 실제 자름 높이와 달라 영어처럼 표가 높아지면 중간이 잘린다.
 */
export const REPORT_PDF_PAGE = {
  /** A4 세로/가로 (mm). html2pdf canvas 슬라이스와 동일 비율 */
  a4Ratio: 297 / 210,
  /**
   * 페이지 하단 여유(CSS px). 테두리·반올림으로 1줄이 잘릴 때 키운다.
   * 너무 크면 아래 빈 여백이 늘어난다.
   */
  bottomSafetyPx: 28,
  /** 이미지 로드 대기 상한 */
  imageWaitMs: 8000,
  /** html2pdf 컨테이너와 맞출 본문 너비 (A4) */
  contentWidthCss: '210mm',
  /** 한 페이지에 통째로 있어야 하는 블록 (안쪽 더 작은 블록이 있으면 바깥은 제외) */
  keepTogetherSelectors: [
    '.defect-detail-table',
    '.report-table',
    '.legal-notice',
    '.document-info-table',
    '.appendix-title'
  ],
  /** 새 장 시작 (기존 page-break-before) */
  forceNewPageSelectors: ['.page-break-before', '.pdf-page-start'],
  /**
   * 페이지 번호용 하단 여백 (mm). 본문 잘림 계산에서 빼 둔다.
   */
  footerMm: 10
};

/**
 * html2pdf가 캔버스를 자르는 높이를 CSS px로 환산한다.
 * @param {number} contentWidthPx
 * @param {{ a4Ratio?: number, scale?: number, bottomSafetyPx?: number }} [opts]
 * @returns {number}
 */
export function cssPageHeightPx(contentWidthPx, opts = {}) {
  const width = Number(contentWidthPx);
  if (!Number.isFinite(width) || width <= 0) return 1;
  const ratio = opts.a4Ratio || REPORT_PDF_PAGE.a4Ratio;
  const scale = Number(opts.scale) > 0 ? Number(opts.scale) : 1;
  const safety =
    opts.bottomSafetyPx == null ? REPORT_PDF_PAGE.bottomSafetyPx : Number(opts.bottomSafetyPx);
  // html2pdf: pxPageHeight = floor(canvas.width * ratio), canvas.width ≈ floor(cssWidth * scale)
  const canvasWidth = Math.floor(width * scale);
  const canvasPage = Math.floor(canvasWidth * ratio);
  return Math.max(1, canvasPage / scale - (Number.isFinite(safety) ? safety : 0));
}

/**
 * 현재 오프셋에서 다음 페이지 시작까지 채울 높이.
 * 이미 페이지 시작이면 0.
 * @param {number} offsetTop
 * @param {number} pageHeightPx
 * @returns {number}
 */
export function spacerHeightToNextPage(offsetTop, pageHeightPx) {
  const pageH = Number(pageHeightPx);
  const top = Number(offsetTop);
  if (!Number.isFinite(pageH) || pageH <= 0 || !Number.isFinite(top) || top < 0) return 0;
  const used = top % pageH;
  if (used === 0) return 0;
  return pageH - used;
}

/**
 * 블록이 현재 페이지 잔여 높이에 안 들어가면 다음 페이지로 밀지 여부.
 * 페이지보다 큰 블록은 밀지 않는다(잘릴 수밖에 없음).
 * @param {number} offsetTop
 * @param {number} height
 * @param {number} pageHeightPx
 * @param {{ forceNewPage?: boolean }} [flags]
 * @returns {boolean}
 */
export function shouldPushToNextPage(offsetTop, height, pageHeightPx, flags = {}) {
  const pageH = Number(pageHeightPx);
  const h = Number(height);
  if (!Number.isFinite(pageH) || pageH <= 0) return false;
  if (flags.forceNewPage) {
    return spacerHeightToNextPage(offsetTop, pageH) > 0;
  }
  if (!Number.isFinite(h) || h <= 0) return false;
  if (h > pageH) return false;
  const rem = spacerHeightToNextPage(offsetTop, pageH);
  const remaining = rem === 0 ? pageH : rem;
  return h > remaining;
}

/**
 * 한 장보다 큰 표는 행 단위로 나눈다. (행 중간 잘림 방지)
 * @param {number} blockHeight
 * @param {number} pageHeightPx
 * @returns {boolean}
 */
export function shouldExpandKeepBlock(blockHeight, pageHeightPx) {
  const h = Number(blockHeight);
  const pageH = Number(pageHeightPx);
  return Number.isFinite(h) && Number.isFinite(pageH) && pageH > 0 && h > pageH;
}

/**
 * keep-together 대상. 안에 더 작은 keep 블록이 있으면 바깥은 제외.
 * @param {ParentNode} root
 * @param {{ keepTogetherSelectors?: string[] }} [opts]
 * @returns {Element[]}
 */
export function collectKeepTogetherElements(root, opts = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') return [];
  const keepSels = opts.keepTogetherSelectors || REPORT_PDF_PAGE.keepTogetherSelectors;
  const joined = keepSels.join(', ');
  const els = Array.prototype.slice.call(root.querySelectorAll(joined)).filter((el) => {
    const inner = el.querySelector(joined);
    return !inner;
  });
  Array.prototype.forEach.call(root.querySelectorAll('.report-table'), (section) => {
    if (!section.querySelector('.defect-detail-table')) return;
    const heading = section.querySelector('h2, h3');
    if (heading && els.indexOf(heading) === -1) els.push(heading);
  });
  return els;
}

/**
 * @param {Element} el
 * @param {number} pageHeightPx
 * @returns {Element[]}
 */
export function expandKeepUnits(el, pageHeightPx) {
  if (!el) return [];
  const height = el.getBoundingClientRect ? el.getBoundingClientRect().height : 0;
  if (!shouldExpandKeepBlock(height, pageHeightPx)) return [el];
  const rows = el.querySelectorAll ? el.querySelectorAll('table tr') : [];
  if (!rows.length) return [el];
  const units = [];
  const heading = el.querySelector ? el.querySelector('h2, h3') : null;
  if (heading) units.push(heading);
  Array.prototype.forEach.call(rows, (row) => units.push(row));
  return units;
}

function offsetTopWithin(root, el) {
  const rootBox = root.getBoundingClientRect();
  const box = el.getBoundingClientRect();
  return box.top - rootBox.top;
}

/**
 * 캡처 루트 기준 keep 블록 (CSS px). 첫 결함 표는 섹션 제목과 한 덩어리.
 * @param {HTMLElement} root
 * @param {{ scale?: number }} [opts]
 * @returns {{ top: number, height: number, rootWidth: number }[]}
 */
export function collectPdfKeepBlocks(root, opts = {}) {
  if (!root || typeof root.getBoundingClientRect !== 'function') return [];
  const rootWidth = root.getBoundingClientRect().width || 1;
  const pageH = cssPageHeightPx(rootWidth, opts);
  const blocks = [];

  collectKeepTogetherElements(root, opts).forEach((el) => {
    expandKeepUnits(el, pageH).forEach((unit) => {
      let top = offsetTopWithin(root, unit);
      let height = unit.getBoundingClientRect().height;
      if (
        unit.classList &&
        unit.classList.contains('defect-detail-table') &&
        typeof unit.closest === 'function'
      ) {
        const section = unit.closest('.report-table');
        const first = section && section.querySelector('.defect-detail-table');
        if (first === unit && section) {
          const heading = section.querySelector('h2, h3');
          if (heading) {
            const ht = offsetTopWithin(root, heading);
            const hh = heading.getBoundingClientRect().height;
            const bottom = Math.max(top + height, ht + hh);
            top = Math.min(top, ht);
            height = bottom - top;
          }
        }
      }
      if (height > 1) {
        blocks.push({ top, height, rootWidth });
      }
    });
  });

  return blocks;
}

/**
 * 캔버스에서 블록을 가로지르지 않게 페이지 y범위를 정한다.
 * @param {number} canvasHeight
 * @param {number} maxPageHeight
 * @param {{ top: number, height: number }[]} blocks 캔버스 px
 * @returns {{ y: number, height: number }[]}
 */
export function planCanvasPageSlices(canvasHeight, maxPageHeight, blocks) {
  const H = Math.max(1, Math.floor(Number(canvasHeight) || 1));
  const P = Math.max(1, Math.floor(Number(maxPageHeight) || 1));
  const sorted = (blocks || [])
    .map((b) => ({
      top: Math.max(0, Math.round(Number(b.top) || 0)),
      bottom: Math.max(0, Math.round((Number(b.top) || 0) + (Number(b.height) || 0)))
    }))
    .filter((b) => b.bottom > b.top)
    .sort((a, b) => a.top - b.top);

  const slices = [];
  let y = 0;
  while (y < H) {
    let end = Math.min(y + P, H);
    let cutAt = end;
    for (let i = 0; i < sorted.length; i++) {
      const b = sorted[i];
      if (b.top <= y) continue;
      if (b.top >= end) continue;
      if (b.bottom <= end) continue;
      const blockH = b.bottom - b.top;
      if (blockH <= P) {
        cutAt = Math.min(cutAt, b.top);
      }
    }
    if (cutAt <= y) {
      cutAt = Math.min(y + P, H);
    }
    slices.push({ y, height: cutAt - y });
    y = cutAt;
  }
  return slices;
}

/**
 * @param {{ top: number, height: number, rootWidth: number }[]} cssBlocks
 * @param {number} canvasWidth
 * @returns {{ top: number, height: number }[]}
 */
export function cssBlocksToCanvasBlocks(cssBlocks, canvasWidth) {
  const width = Number(canvasWidth);
  if (!Number.isFinite(width) || width <= 0) return [];
  return (cssBlocks || []).map((b) => {
    const rootW = Number(b.rootWidth) > 0 ? Number(b.rootWidth) : width;
    const k = width / rootW;
    return {
      top: Math.round(Number(b.top) * k),
      height: Math.round(Number(b.height) * k)
    };
  });
}

function tableColumnCount(tr) {
  const table = tr.closest && tr.closest('table');
  const rows = table && table.rows ? table.rows : tr.parentNode ? [tr] : [];
  let max = 1;
  for (let i = 0; i < rows.length; i++) {
    const cells = rows[i].cells;
    if (!cells) continue;
    let n = 0;
    for (let j = 0; j < cells.length; j++) {
      n += Number(cells[j].colSpan) || 1;
    }
    if (n > max) max = n;
  }
  return max;
}

function insertSpacerBefore(el, heightPx) {
  if (!el || !el.parentNode || heightPx <= 0) return;
  const doc = el.ownerDocument;
  if (el.tagName === 'TR') {
    const padRow = doc.createElement('tr');
    padRow.className = 'pdf-page-spacer-row';
    padRow.setAttribute('aria-hidden', 'true');
    const td = doc.createElement('td');
    td.colSpan = tableColumnCount(el);
    td.style.cssText = `height:${heightPx}px;padding:0;margin:0;border:0;background:#ffffff;line-height:0;font-size:0;`;
    padRow.appendChild(td);
    el.parentNode.insertBefore(padRow, el);
    return;
  }
  const pad = doc.createElement('div');
  pad.className = 'pdf-page-spacer';
  pad.setAttribute('aria-hidden', 'true');
  pad.style.cssText = `display:block;width:100%;height:${heightPx}px;margin:0;padding:0;border:0;`;
  el.parentNode.insertBefore(pad, el);
}

function waitForImages(root, timeoutMs) {
  const imgs = root.querySelectorAll ? Array.prototype.slice.call(root.querySelectorAll('img')) : [];
  if (!imgs.length) return Promise.resolve();
  return new Promise((resolve) => {
    let left = imgs.length;
    const done = () => {
      left -= 1;
      if (left <= 0) resolve();
    };
    const timer = setTimeout(resolve, timeoutMs);
    imgs.forEach((img) => {
      if (img.complete) {
        done();
        return;
      }
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
    Promise.resolve().then(() => {
      if (left <= 0) clearTimeout(timer);
    });
  });
}

function disableNativeCssBreaks(root) {
  if (!root.querySelectorAll) return;
  const els = root.querySelectorAll('.page-break-before, .pdf-page-start');
  Array.prototype.forEach.call(els, (el) => {
    el.style.pageBreakBefore = 'auto';
    el.style.breakBefore = 'auto';
    el.style.pageBreakInside = 'auto';
    el.style.breakInside = 'auto';
  });
}

function applyKeepTogetherSpacers(root, pageH, opts = {}) {
  const forceSels = opts.forceNewPageSelectors || REPORT_PDF_PAGE.forceNewPageSelectors;
  const keepEls = collectKeepTogetherElements(root, opts);
  const forceEls = Array.prototype.slice.call(root.querySelectorAll(forceSels.join(', ')));

  const recs = new Map();
  forceEls.forEach((el) => {
    recs.set(el, { el, force: true, keep: false });
  });
  keepEls.forEach((el) => {
    expandKeepUnits(el, pageH).forEach((unit) => {
      const rec = recs.get(unit) || { el: unit, force: false, keep: false };
      rec.keep = true;
      recs.set(unit, rec);
    });
  });

  const ordered = Array.from(recs.values()).sort((a, b) => {
    const pos = a.el.compareDocumentPosition(b.el);
    if (pos & 4) return -1;
    if (pos & 2) return 1;
    return 0;
  });

  ordered.forEach((rec) => {
    const el = rec.el;
    if (!el || !el.parentNode) return;
    if (el.classList && (el.classList.contains('pdf-page-spacer') || el.classList.contains('pdf-page-spacer-row'))) {
      return;
    }
    let top = offsetTopWithin(root, el);
    const height = el.getBoundingClientRect().height;
    if (rec.force && shouldPushToNextPage(top, height, pageH, { forceNewPage: true })) {
      insertSpacerBefore(el, spacerHeightToNextPage(top, pageH));
      top = offsetTopWithin(root, el);
    }
    if (rec.keep && shouldPushToNextPage(top, height, pageH)) {
      insertSpacerBefore(el, spacerHeightToNextPage(top, pageH));
    }
  });
}

/**
 * 레이아웃 측정 후 잘리지 않게 스페이서를 넣는다. (DOM 필요)
 * skipAttach: html2canvas onclone처럼 이미 캡처 트리에 있을 때. 좌표 원점은 이 root(캔버스 상단).
 * @param {HTMLElement} root
 * @param {{ widthPx?: number, scale?: number, skipAttach?: boolean, skipImageWait?: boolean }} opts
 */
export async function layoutReportPdfPageBreaks(root, opts = {}) {
  if (!root || typeof document === 'undefined') return root;

  disableNativeCssBreaks(root);

  const skipAttach = !!opts.skipAttach;
  let host = null;
  const prevPos = root.style.position;
  if (!skipAttach) {
    host = document.createElement('div');
    host.setAttribute('data-pdf-pagebreak-host', 'true');
    host.style.cssText = 'position:absolute;left:-12000px;top:0;';
    root.style.position = 'relative';
    root.style.width = opts.contentWidthCss || REPORT_PDF_PAGE.contentWidthCss;
    host.appendChild(root);
    document.body.appendChild(host);
  }

  try {
    if (!opts.skipImageWait) {
      await waitForImages(root, opts.imageWaitMs || REPORT_PDF_PAGE.imageWaitMs);
    }
    const widthPx = Number(opts.widthPx) || root.getBoundingClientRect().width || 794;
    const pageH = cssPageHeightPx(widthPx, opts);
    applyKeepTogetherSpacers(root, pageH, opts);
    root.dataset.pdfLayoutWidth = String(Math.round(widthPx));
  } finally {
    if (host && host.parentNode) host.parentNode.removeChild(host);
    if (!skipAttach) {
      root.style.position = prevPos;
      root.style.width = opts.contentWidthCss || REPORT_PDF_PAGE.contentWidthCss;
    }
  }

  return root;
}
